/* <faq-page> — category scrollspy + client-side answer filter.
   Open/close behavior itself comes from <collapsible-section> (base.js);
   this element only coordinates the nav rail and the search box. */

class FaqPage extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll("[data-faq-item]"));
    this.groups = Array.from(this.querySelectorAll("[data-faq-group]"));
    this.navItems = Array.from(this.querySelectorAll("[data-faq-nav-item]"));
    this.searchInput = this.querySelector("[data-faq-search]");
    this.emptyNote = this.querySelector("[data-faq-empty]");

    // Question + answer text is in the DOM even while collapsed
    // (grid-rows trick), so textContent is a complete haystack.
    this.haystacks = new Map();
    this.items.forEach((item) => {
      this.haystacks.set(
        item,
        item.textContent.toLowerCase().replace(/\s+/g, " "),
      );
    });

    if (this.searchInput) {
      this.handleSearch = this.handleSearch.bind(this);
      this.searchInput.addEventListener("input", this.handleSearch);
      this.searchInput.addEventListener("search", this.handleSearch);
    }

    if (this.navItems.length && "IntersectionObserver" in window) {
      this.initScrollspy();
    }
  }

  disconnectedCallback() {
    clearTimeout(this.searchTimer);
    if (this.observer) this.observer.disconnect();
  }

  initScrollspy() {
    // Narrow horizontal band around the upper third of the viewport:
    // whichever group crosses it becomes the active nav entry.
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.setActiveNav(entry.target.id);
        });
      },
      { rootMargin: "-25% 0px -65% 0px" },
    );
    this.groups.forEach((group) => this.observer.observe(group));
  }

  setActiveNav(id) {
    this.navItems.forEach((item) => {
      const link = item.querySelector("a");
      if (!link) return;
      const active = link.getAttribute("href") === `#${id}`;
      link.classList.toggle("wt-faq__nav-link--active", active);
      if (active) {
        link.setAttribute("aria-current", "true");
      } else {
        link.removeAttribute("aria-current");
      }
    });
  }

  handleSearch() {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.filter(this.searchInput.value);
    }, 120);
  }

  filter(rawQuery) {
    const query = rawQuery.trim().toLowerCase();
    let anyVisible = false;

    this.items.forEach((item) => {
      const match = query === "" || this.haystacks.get(item).includes(query);
      item.hidden = !match;
      if (match) anyVisible = true;

      if (query === "") {
        // Back to the authored default state.
        this.setOpen(item, item.dataset.faqDefaultOpen === "true");
      } else if (match) {
        // Expand matches so the found text is actually visible.
        this.setOpen(item, true);
      }
    });

    this.groups.forEach((group) => {
      group.hidden = !group.querySelector("[data-faq-item]:not([hidden])");
    });

    this.navItems.forEach((item) => {
      const group = document.getElementById(item.dataset.faqNavItem);
      item.hidden = Boolean(group && group.hidden);
    });

    if (this.emptyNote) this.emptyNote.hidden = anyVisible;
  }

  setOpen(item, open) {
    item.dataset.open = open ? "true" : "false";
    const trigger = item.querySelector(".wt-collapse__trigger");
    if (!trigger) return;
    trigger.classList.toggle("wt-collapse__trigger--active", open);
    trigger.setAttribute("aria-expanded", open ? "true" : "false");
  }
}

if (!customElements.get("faq-page")) {
  customElements.define("faq-page", FaqPage);
}
